import type { AppLogContext } from '#ui-shared/utils/logger/contexts/log-contexts';

/**
 * This interface defines the structure of a logger adapter, which is an abstraction for logging messages, errors, warnings, and informational logs. Each method corresponds to a log level and accepts a message and an optional context object that can contain additional information about the log.
 */
export interface LoggerAdapter {
  log: (message: string, context?: AppLogContext) => void;
  error: (message: string, context?: AppLogContext) => void;
  warn: (message: string, context?: AppLogContext) => void;
  info: (message: string, context?: AppLogContext) => void;
  debug: (message: string, context?: AppLogContext) => void;
}

export interface LoggerOptions {
  /** Whether the current deployment environment is considered sensitive. Will not log sensitive logs if set to true. */
  isSensitiveEnvironment: boolean;
  /**
   * The log level to use for logging. Logs with a level lower than the specified level will not be logged. Will default to info.
   *
   * Should be initialized with an environment variable, e.g. VITE_FRONTEND_LOG_LEVEL, to allow configuring the log level without changing the code. This can be useful for enabling more verbose logging in development and reducing log noise in production.
   */
  logLevel?: 'debug' | 'info' | 'warn' | 'error';
}

/**
 * Defines the structure of the input for logging messages.
 *
 * {@link 'context'} Represents ordinary contextual data that is not sensitive, and can be logged in all environments.
 *
 * {@link 'sensitiveContext'} - Represents sensitive contextual data that should be redacted in sensitive environments.
 *
 */
export interface LoggerInput {
  message: string;
  context?: AppLogContext;
  sensitiveContext?: AppLogContext;
}

const FRONTEND_LOG_LEVEL = setLogLevelFromEnv();
const FRONTEND_IS_SENSITIVE_ENV = setIsSensitiveEnvFromEnv();

/**
 * An abstraction for logging messages, errors, and other information. This class can be extended to implement different logging adapters, see: {@link LoggerAdapter}.
 *
 * On logging, it will call all registered adapters to log the message.
 *
 * It also provides methods for logging sensitive information, which will only log if the environment is not marked as sensitive.
 */
class Logger {
  private static instance: Logger;
  private adapters: LoggerAdapter[] = [];

  private constructor(private readonly config: LoggerOptions) {}

  /**
   * Gets the singleton instance of the Logger. If the instance does not exist yet, it will be created with the configuration provided in environment variables.
   * @returns The singleton instance of the Logger.
   */
  static getInstance(): Logger {
    if (!Logger.instance) {
      Logger.instance = new Logger({ isSensitiveEnvironment: FRONTEND_IS_SENSITIVE_ENV, logLevel: FRONTEND_LOG_LEVEL });
    }
    return Logger.instance;
  }

  /**
   * Registers a new logger adapter. The adapter should implement the LoggerAdapter interface. Once registered, the logger will use this adapter to log messages.
   * @param adapter
   */
  registerAdapter(adapter: LoggerAdapter): void {
    this.adapters.push(adapter);
  }

  /**
   * Removes a logger adapter. Once removed, the logger will no longer use this adapter to log messages.
   * @param adapterToRemove
   */
  removeAdapter(adapterToRemove: LoggerAdapter): void {
    this.adapters = this.adapters.filter(existingAdapter => existingAdapter !== adapterToRemove);
  }

  /**
   * Helper method to build the final context object based on the current environment.
   * If the environment is sensitive, the sensitiveContext is stripped.
   * If the environment is not sensitive, the context and sensitiveContext are merged.
   */
  private buildContext(input: LoggerInput): AppLogContext | undefined {
    if (this.config.isSensitiveEnvironment) {
      return input.context;
    }

    if (!input.context && !input.sensitiveContext) {
      return undefined;
    }

    return {
      ...input.context,
      ...input.sensitiveContext,
    };
  }

  /**
   * Logs a message with the specified log level and context. The message will be logged using all registered adapters.
   *
   * Requires log level to be 'debug' or 'info' to log. For 'warn' and 'error' levels, use the corresponding methods {@link warn} and {@link error}.
   *
   * The context can be used to provide additional information about the log, such as user ID, operation details, or any other relevant data that can help with debugging and monitoring.
   *
   * @param input - The logger input containing the message, optional context, and optional sensitiveContext.
   */
  log(input: LoggerInput): void {
    if (this.config.logLevel === 'debug' || this.config.logLevel === 'info') {
      const context = this.buildContext(input);
      for (const adapter of this.adapters) {
        adapter.log(input.message, context);
      }
    }
  }

  /**
   * Logs an error message with the specified context. The error message will be logged using all registered adapters.
   *
   * Will be logged at all log levels.
   *
   * The context can be used to provide additional information about the error, such as user ID, operation details, or any other relevant data that can help with debugging and monitoring.
   *
   * @param input - The logger input containing the message, optional context, and optional sensitiveContext.
   */
  error(input: LoggerInput): void {
    const context = this.buildContext(input);
    for (const adapter of this.adapters) {
      adapter.error(input.message, context);
    }
  }

  /**
   * Logs a warning message with the specified context. The warning message will be logged using all registered adapters.
   *
   * Requires log level to be 'debug', 'info' or 'warn' to log. For 'error' level, use the corresponding method {@link error}.
   *
   * The context can be used to provide additional information about the warning, such as user ID, operation details, or any other relevant data that can help with debugging and monitoring.
   *
   * @param input - The logger input containing the message, optional context, and optional sensitiveContext.
   */
  warn(input: LoggerInput): void {
    if (this.config.logLevel === 'debug' || this.config.logLevel === 'info' || this.config.logLevel === 'warn') {
      const context = this.buildContext(input);
      for (const adapter of this.adapters) {
        adapter.warn(input.message, context);
      }
    }
  }

  /**
   * Logs an informational message with the specified context. The informational message will be logged using all registered adapters.
   *
   * Requires log level to be 'debug' or 'info' to log. For 'warn' and 'error' levels, use the corresponding methods {@link warn} and {@link error}.
   *
   * The context can be used to provide additional information about the log, such as user ID, operation details, or any other relevant data that can help with debugging and monitoring.
   *
   * @param input - The logger input containing the message, optional context, and optional sensitiveContext.
   */
  info(input: LoggerInput): void {
    if (this.config.logLevel === 'debug' || this.config.logLevel === 'info') {
      const context = this.buildContext(input);
      for (const adapter of this.adapters) {
        adapter.info(input.message, context);
      }
    }
  }

  /**
   * Logs a debug message with the specified context. The debug message will be logged using all registered adapters.
   *
   * Requires log level to be 'debug' to log. For 'info', 'warn' and 'error' levels, use the corresponding methods {@link info}, {@link warn} and {@link error}.
   *
   * The context can be used to provide additional information about the log, such as user ID, operation details, or any other relevant data that can help with debugging and monitoring.
   *
   * @param input - The logger input containing the message, optional context, and optional sensitiveContext.
   */
  debug(input: LoggerInput): void {
    if (this.config.logLevel === 'debug') {
      const context = this.buildContext(input);
      for (const adapter of this.adapters) {
        adapter.debug(input.message, context);
      }
    }
  }
}

function setLogLevelFromEnv(): LoggerOptions['logLevel'] {
  const logLevel = import.meta.env.VITE_FRONTEND_LOG_LEVEL as LoggerOptions['logLevel'] | undefined;

  if (!logLevel || !['debug', 'info', 'warn', 'error'].includes(logLevel)) {
    return 'info';
  }

  return logLevel;
}

function setIsSensitiveEnvFromEnv(): boolean {
  const isSensitiveEnv = import.meta.env.VITE_FRONTEND_SENSITIVE_ENV;

  if (isSensitiveEnv === undefined) {
    return false;
  }

  try {
    return JSON.parse(isSensitiveEnv);
  }
  catch (error) {
    console.warn(`Failed to parse VITE_FRONTEND_SENSITIVE_ENV value "${isSensitiveEnv}". Defaulting to false.`, error);
    return false;
  }
}

/**
 * The singleton instance of the Logger class, which can be imported and used throughout the application for logging messages, errors, warnings, and informational logs. The logger will use all registered adapters to log messages, allowing us to customize what backends to use.
 *
 * The logger can be configured using environment variables:
 * - VITE_FRONTEND_LOG_LEVEL: Sets the log level for logging. Valid values are 'debug', 'info', 'warn' and 'error'. Defaults to 'info' if not set or invalid.
 * - VITE_FRONTEND_SENSITIVE_ENV: Sets whether the current environment is considered sensitive. Should be a boolean value ('true' or 'false'). Defaults to false if not set or invalid.
 *
 * Example usage:
 * ```typescript
 * import { appLogger } from 'path-to-logger';
 *
 * appLogger.log('This is an informational message');
 * appLogger.error('This is an error message with attached data', { errorCode: 123 });
 * ```
 *
 * In this example, the first two logs will be logged based on the configured log level, while the third log will only be logged if the environment is not marked as sensitive.
 */
export const appLogger = Logger.getInstance();
